import loadable, { LoadableComponent } from '@loadable/component';
import React, { createContext, useEffect } from 'react';
import { useChain, useSpringRef, SpringRef } from 'react-spring';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { FaqSchema } from 'utils/constants/jsonLdSchema';
import { useScrolledIntoViewOnce } from 'utils/hooks';
import {
  HeroSectionType,
  PageSectionType,
  ProductsBySlugType,
} from 'utils/types';

import Layout from '../components/Layout';
import { SEO } from '../components/SEO';
import { BusinessPartnerContainerProps } from '../containers/Homepage/BusinessPartnerContainer';
import { ByTheNumbersContainerProps } from '../containers/Homepage/ByTheNumbersContainer';
import { FAQContainerProps } from '../containers/Homepage/FAQContainer';
import {
  HeroContainer as HeroSection,
  HeroContainerProps,
} from '../containers/Homepage/HeroContainer';
import { HowItWorksContainerProps } from '../containers/Homepage/HowItWorksContainer';
import { LatestUpdatesContainerProps } from '../containers/Homepage/LatestUpdatesContainer';
import {
  PopularTestsContainer as PopularTestsSection,
  PopularTestsContainerProps,
} from '../containers/Homepage/PopularTestsContainer';
import { PressContainerProps } from '../containers/Homepage/PressContainer';
import { RecommendedArticlesContainerProps } from '../containers/Homepage/RecommendedArticlesContainer';
import { TestimonialContainerProps } from '../containers/Homepage/TestimonialContainer';
import { WhatToExpectContainerProps } from '../containers/Homepage/WhatToExpectContainer';
import useDisplayedSectionFilterForMembers from '../utils/hooks/useDisplayedSectionFilterForMembers';

const BusinessPartnerSection = loadable(
  () => import('../containers/Homepage/BusinessPartnerContainer'),
);
const ByTheNumbersSection = loadable(
  () => import('../containers/Homepage/ByTheNumbersContainer'),
);
const FAQSection = loadable(
  () => import('../containers/Homepage/FAQContainer'),
);
const HowItWorksSection = loadable(
  () => import('../containers/Homepage/HowItWorksContainer'),
);
const LatestUpdatesSection = loadable(
  () => import('../containers/Homepage/LatestUpdatesContainer'),
);
const PressSection = loadable(
  () => import('../containers/Homepage/PressContainer'),
);
const RecommendedArticlesSection = loadable(
  () => import('../containers/Homepage/RecommendedArticlesContainer'),
);
const TestimonialSection = loadable(
  () => import('../containers/Homepage/TestimonialContainer'),
);
const WhatToExpectSection = loadable(
  () => import('../containers/Homepage/WhatToExpectContainer'),
);

const components: {
  HeroSection: React.FunctionComponent<HeroContainerProps>;
  PopularTestsSection: React.FunctionComponent<PopularTestsContainerProps>;
  HowItWorksSection: LoadableComponent<HowItWorksContainerProps>;
  WhatToExpectSection: LoadableComponent<WhatToExpectContainerProps>;
  LatestUpdatesSection: LoadableComponent<LatestUpdatesContainerProps>;
  TestimonialSection: LoadableComponent<TestimonialContainerProps>;
  ByTheNumbersSection: LoadableComponent<ByTheNumbersContainerProps>;
  RecommendedArticlesSection: LoadableComponent<RecommendedArticlesContainerProps>;
  PressSection: LoadableComponent<PressContainerProps>;
  BusinessPartnerSection: LoadableComponent<BusinessPartnerContainerProps>;
  FAQSection: LoadableComponent<FAQContainerProps>;
} = {
  HeroSection,
  PopularTestsSection,
  HowItWorksSection,
  WhatToExpectSection,
  LatestUpdatesSection,
  TestimonialSection,
  BusinessPartnerSection,
  ByTheNumbersSection,
  RecommendedArticlesSection,
  PressSection,
  FAQSection,
};

interface ProductPromoData {
  slug: string;
  onSale: boolean;
  promoText: string;
}

interface PageContext {
  heroSection: HeroSectionType;
  sections: PageSectionType[];
  productsBySlug: ProductsBySlugType;
  componentName: string;
  title: string;
  faqSchema?: FaqSchema;
  allProductPromoData: ProductPromoData[];
}

interface Props {
  pageContext: PageContext;
}

export interface RefsContextType {
  [key: string]: SpringRef;
}

export const ProductInfoContext = createContext<ProductsBySlugType>({});
export const RefsForAnimationContext = createContext<RefsContextType>({});
export const AllProductPromosContext = createContext<ProductPromoData[]>([]);

export const homepageTemplate: React.FC<Props> = ({ pageContext }) => {
  const { sections, heroSection, productsBySlug, allProductPromoData } =
    pageContext;

  if (!sections || !heroSection) {
    return null;
  }

  // Refs will be passed down in RefsForAnimationContext
  const animation1 = useSpringRef();
  const animation2 = useSpringRef();
  const animation3 = useSpringRef();
  const animation4 = useSpringRef();

  // by default useChain will space each animation 500ms apart
  // the 2nd argument overrides this default and defines the timing of the animations:
  // 1st animation happens immediately - 2nd animation starts 450ms after first animation ends
  // 3rd and 4th animations start 750ms after the first animation ends
  useChain([animation1, animation2, animation3, animation4], [0, 0.2, 1, 1]);

  // displayedSections is an array of PageSectionType
  // If the user is an active member, we filter out sections based on the slug
  const [displayedSections] = useDisplayedSectionFilterForMembers(sections);
  useEffect(() => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.VIEWED_PAGE,
      data: {
        Page: 'Homepage',
      },
    });
  }, []);

  const trackScrolledIntoView = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.SCROLLED_TO_BOTTOM,
      data: {
        label: ANALYTICS.LABELS.HOMEPAGE,
      },
    });
  };

  const footerRef = useScrolledIntoViewOnce(trackScrolledIntoView);

  return (
    <>
      <SEO />
      <Layout productsBySlug={productsBySlug} footerRef={footerRef}>
        <ProductInfoContext.Provider value={productsBySlug}>
          <AllProductPromosContext.Provider value={allProductPromoData}>
            <RefsForAnimationContext.Provider
              value={{ animation1, animation2, animation3, animation4 }}
            >
              <HeroSection content={heroSection} />
              {displayedSections.map((section, i: number) => {
                // @ts-ignore
                if (components[section.componentName]) {
                  return React.createElement(
                    // @ts-ignore
                    components[section.componentName],
                    {
                      content: section,
                      key: `${section.componentName}-${i}`, // handles the case for duplicate section keys
                    },
                  );
                }
              })}
            </RefsForAnimationContext.Provider>
          </AllProductPromosContext.Provider>
        </ProductInfoContext.Provider>
      </Layout>
    </>
  );
};

export default homepageTemplate;
